import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';

const ProfileEdit = () => {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showPasswordSuccessMessage, setShowPasswordSuccessMessage] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: 'John',
    lastName: 'Doe',
    email: 'john@jewelthread.com',
    phone: '+1 (555) 123-4567',
    
    // Company Information
    companyName: 'JewelThread Studio',
    businessAddress: {
      street: '123 Design Avenue',
      city: 'New York',
      state: 'NY',
      zipCode: '10001',
      country: 'United States'
    },
    
    // Professional Information
    jobTitle: 'Creative Director',
    department: 'Design',
    website: 'https://jewelthread.com',
    taxId: '12-3456789',
    
    // Business Details
    businessType: 'LLC',
    yearsInBusiness: '5',
    specialization: 'Custom Engagement Rings',
    teamSize: '10-25',
    
    // Communication Preferences
    notifications: {
      email: true,
      sms: false,
      project_updates: true,
      marketing: false
    }
  });

  // Password state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});
  const [passwordErrors, setPasswordErrors] = useState({});

  // Handle input changes
  const handleInputChange = (field, value, nested = null) => {
    setFormData(prev => {
      if (nested) {
        return {
          ...prev,
          [nested]: {
            ...prev?.[nested],
            [field]: value
          }
        };
      }
      return {
        ...prev,
        [field]: value
      };
    });

    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Handle password input changes
  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (passwordErrors?.[field]) {
      setPasswordErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Password validation
  const validatePassword = () => {
    const newErrors = {};

    // Required fields
    if (!passwordData?.currentPassword?.trim()) {
      newErrors.currentPassword = 'Current password is required';
    }
    if (!passwordData?.newPassword?.trim()) {
      newErrors.newPassword = 'New password is required';
    }
    if (!passwordData?.confirmPassword?.trim()) {
      newErrors.confirmPassword = 'Please confirm your new password';
    }

    // Password strength validation
    if (passwordData?.newPassword && passwordData?.newPassword?.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters long';
    }

    // Password complexity check
    if (passwordData?.newPassword) {
      const hasUpperCase = /[A-Z]/?.test(passwordData?.newPassword);
      const hasLowerCase = /[a-z]/?.test(passwordData?.newPassword);
      const hasNumbers = /\d/?.test(passwordData?.newPassword);
      const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/?.test(passwordData?.newPassword);

      if (!hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChar) {
        newErrors.newPassword = 'Password must contain uppercase, lowercase, number, and special character';
      }
    }

    // Password confirmation
    if (passwordData?.newPassword && passwordData?.confirmPassword && 
        passwordData?.newPassword !== passwordData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Check if new password is different from current
    if (passwordData?.currentPassword && passwordData?.newPassword && 
        passwordData?.currentPassword === passwordData?.newPassword) {
      newErrors.newPassword = 'New password must be different from current password';
    }

    setPasswordErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  // Handle password reset
  const handlePasswordReset = async (e) => {
    e?.preventDefault();
    
    if (!validatePassword()) {
      return;
    }

    setIsChangingPassword(true);

    try {
      // Simulate API call for password change
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      console.log('Password changed successfully');
      
      // Clear password form
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      setShowPasswordSuccessMessage(true);
      setTimeout(() => {
        setShowPasswordSuccessMessage(false);
      }, 3000);
      
    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordErrors({ submit: 'Failed to change password. Please try again.' });
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Validation
  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData?.firstName?.trim()) newErrors.firstName = 'First name is required';
    if (!formData?.lastName?.trim()) newErrors.lastName = 'Last name is required';
    if (!formData?.email?.trim()) newErrors.email = 'Email is required';
    if (!formData?.companyName?.trim()) newErrors.companyName = 'Company name is required';

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData?.email && !emailRegex?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone validation (if provided)
    if (formData?.phone && formData?.phone?.length < 10) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    // Business address validation
    if (!formData?.businessAddress?.street?.trim()) {
      newErrors['businessAddress.street'] = 'Street address is required';
    }
    if (!formData?.businessAddress?.city?.trim()) {
      newErrors['businessAddress.city'] = 'City is required';
    }
    if (!formData?.businessAddress?.state?.trim()) {
      newErrors['businessAddress.state'] = 'State is required';
    }
    if (!formData?.businessAddress?.zipCode?.trim()) {
      newErrors['businessAddress.zipCode'] = 'ZIP code is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      console.log('Profile updated:', formData);
      
      setShowSuccessMessage(true);
      setTimeout(() => {
        setShowSuccessMessage(false);
        navigate('/dashboard');
      }, 2000);
      
    } catch (error) {
      console.error('Error updating profile:', error);
      setErrors({ submit: 'Failed to update profile. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle notification changes
  const handleNotificationChange = (field, checked) => {
    setFormData(prev => ({
      ...prev,
      notifications: {
        ...prev?.notifications,
        [field]: checked
      }
    }));
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Edit Profile - JewelThread</title>
        <meta name="description" content="Edit your profile and business information" />
      </Helmet>
      <Header />
      <main className="pt-16">
        {/* Header */}
        <div className="bg-card border-b border-border">
          <div className="max-w-4xl mx-auto px-6 py-8">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-2 text-sm text-muted-foreground mb-2">
                  <Link to="/dashboard" className="hover:text-accent transition-luxury">Dashboard</Link>
                  <Icon name="ChevronRight" size={16} />
                  <span>Edit Profile</span>
                </div>
                <h1 className="text-3xl font-heading text-foreground">Edit Profile</h1>
                <p className="text-muted-foreground mt-2">
                  Update your personal and business information
                </p>
              </div>
              
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  iconName="X"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Success Messages */}
        {showSuccessMessage && (
          <div className="max-w-4xl mx-auto px-6 py-4">
            <div className="bg-success/10 border border-success/20 rounded-lg p-4 flex items-center space-x-3">
              <Icon name="CheckCircle" size={20} color="var(--color-success)" />
              <p className="text-success font-medium">Profile updated successfully!</p>
            </div>
          </div>
        )}

        {showPasswordSuccessMessage && (
          <div className="max-w-4xl mx-auto px-6 py-4">
            <div className="bg-success/10 border border-success/20 rounded-lg p-4 flex items-center space-x-3">
              <Icon name="Shield" size={20} color="var(--color-success)" />
              <p className="text-success font-medium">Password changed successfully!</p>
            </div>
          </div>
        )}

        {/* Form */}
        <div className="max-w-4xl mx-auto px-6 py-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            
            {/* Personal Information */}
            <div className="bg-card border border-border rounded-lg p-6 elevation-1">
              <h2 className="text-xl font-heading text-foreground mb-6 flex items-center space-x-2">
                <Icon name="User" size={20} />
                <span>Personal Information</span>
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="First Name"
                  required
                  value={formData?.firstName}
                  onChange={(e) => handleInputChange('firstName', e?.target?.value)}
                  error={errors?.firstName}
                />
                
                <Input
                  label="Last Name"
                  required
                  value={formData?.lastName}
                  onChange={(e) => handleInputChange('lastName', e?.target?.value)}
                  error={errors?.lastName}
                />
                
                <Input
                  label="Email Address"
                  type="email"
                  required
                  value={formData?.email}
                  onChange={(e) => handleInputChange('email', e?.target?.value)}
                  error={errors?.email}
                />
                
                <Input
                  label="Phone Number"
                  type="tel"
                  value={formData?.phone}
                  onChange={(e) => handleInputChange('phone', e?.target?.value)}
                  error={errors?.phone}
                  description="Include country code for international numbers"
                />
              </div>
            </div>

            {/* Security Settings */}
            <div className="bg-card border border-border rounded-lg p-6 elevation-1">
              <h2 className="text-xl font-heading text-foreground mb-6 flex items-center space-x-2">
                <Icon name="Shield" size={20} />
                <span>Security Settings</span>
              </h2>
              
              <div className="mb-6">
                <h3 className="text-lg font-medium text-foreground mb-2">Change Password</h3>
                <p className="text-muted-foreground text-sm mb-6">
                  Update your password to keep your account secure. Use a strong password with at least 8 characters.
                </p>
                
                <form onSubmit={handlePasswordReset} className="space-y-4">
                  <Input
                    label="Current Password"
                    type="password"
                    required
                    value={passwordData?.currentPassword}
                    onChange={(e) => handlePasswordChange('currentPassword', e?.target?.value)}
                    error={passwordErrors?.currentPassword}
                    autoComplete="current-password"
                  />
                  
                  <Input
                    label="New Password"
                    type="password"
                    required
                    value={passwordData?.newPassword}
                    onChange={(e) => handlePasswordChange('newPassword', e?.target?.value)}
                    error={passwordErrors?.newPassword}
                    description="Must contain uppercase, lowercase, number, and special character"
                    autoComplete="new-password"
                  />
                  
                  <Input
                    label="Confirm New Password"
                    type="password"
                    required
                    value={passwordData?.confirmPassword}
                    onChange={(e) => handlePasswordChange('confirmPassword', e?.target?.value)}
                    error={passwordErrors?.confirmPassword}
                    autoComplete="new-password"
                  />

                  {/* Password Error */}
                  {passwordErrors?.submit && (
                    <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex items-center space-x-3">
                      <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
                      <p className="text-destructive">{passwordErrors?.submit}</p>
                    </div>
                  )}

                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      disabled={isChangingPassword}
                      iconName={isChangingPassword ? "Loader2" : "Shield"}
                      className={isChangingPassword ? "animate-spin" : ""}
                    >
                      {isChangingPassword ? 'Changing Password...' : 'Change Password'}
                    </Button>
                  </div>
                </form>
              </div>

              {/* Security Information */}
              <div className="border-t border-border pt-6">
                <h3 className="text-lg font-medium text-foreground mb-4">Security Information</h3>
                <div className="space-y-3 text-sm text-muted-foreground">
                  <div className="flex items-center space-x-2">
                    <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                    <span>Two-factor authentication: Not configured</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Clock" size={16} />
                    <span>Last password change: December 15, 2024</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="MapPin" size={16} />
                    <span>Last login from: New York, NY</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Company Information */}
            <div className="bg-card border border-border rounded-lg p-6 elevation-1">
              <h2 className="text-xl font-heading text-foreground mb-6 flex items-center space-x-2">
                <Icon name="Building2" size={20} />
                <span>Company Information</span>
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Company Name"
                  required
                  value={formData?.companyName}
                  onChange={(e) => handleInputChange('companyName', e?.target?.value)}
                  error={errors?.companyName}
                />
                
                <Input
                  label="Job Title"
                  value={formData?.jobTitle}
                  onChange={(e) => handleInputChange('jobTitle', e?.target?.value)}
                />
                
                <Input
                  label="Department"
                  value={formData?.department}
                  onChange={(e) => handleInputChange('department', e?.target?.value)}
                />
                
                <Input
                  label="Website"
                  type="url"
                  value={formData?.website}
                  onChange={(e) => handleInputChange('website', e?.target?.value)}
                  description="Your company website URL"
                />
              </div>
            </div>

            {/* Business Address */}
            <div className="bg-card border border-border rounded-lg p-6 elevation-1">
              <h2 className="text-xl font-heading text-foreground mb-6 flex items-center space-x-2">
                <Icon name="MapPin" size={20} />
                <span>Business Address</span>
              </h2>
              
              <div className="space-y-6">
                <Input
                  label="Street Address"
                  required
                  value={formData?.businessAddress?.street}
                  onChange={(e) => handleInputChange('street', e?.target?.value, 'businessAddress')}
                  error={errors?.['businessAddress.street']}
                />
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Input
                    label="City"
                    required
                    value={formData?.businessAddress?.city}
                    onChange={(e) => handleInputChange('city', e?.target?.value, 'businessAddress')}
                    error={errors?.['businessAddress.city']}
                  />
                  
                  <Input
                    label="State/Province"
                    required
                    value={formData?.businessAddress?.state}
                    onChange={(e) => handleInputChange('state', e?.target?.value, 'businessAddress')}
                    error={errors?.['businessAddress.state']}
                  />
                  
                  <Input
                    label="ZIP/Postal Code"
                    required
                    value={formData?.businessAddress?.zipCode}
                    onChange={(e) => handleInputChange('zipCode', e?.target?.value, 'businessAddress')}
                    error={errors?.['businessAddress.zipCode']}
                  />
                </div>
                
                <Input
                  label="Country"
                  value={formData?.businessAddress?.country}
                  onChange={(e) => handleInputChange('country', e?.target?.value, 'businessAddress')}
                />
              </div>
            </div>

            {/* Business Details */}
            <div className="bg-card border border-border rounded-lg p-6 elevation-1">
              <h2 className="text-xl font-heading text-foreground mb-6 flex items-center space-x-2">
                <Icon name="Briefcase" size={20} />
                <span>Business Details</span>
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Business Type</label>
                  <select
                    value={formData?.businessType}
                    onChange={(e) => handleInputChange('businessType', e?.target?.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value="LLC">LLC</option>
                    <option value="Corporation">Corporation</option>
                    <option value="Sole Proprietorship">Sole Proprietorship</option>
                    <option value="Partnership">Partnership</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Team Size</label>
                  <select
                    value={formData?.teamSize}
                    onChange={(e) => handleInputChange('teamSize', e?.target?.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value="1-5">1-5 employees</option>
                    <option value="6-10">6-10 employees</option>
                    <option value="10-25">10-25 employees</option>
                    <option value="25-50">25-50 employees</option>
                    <option value="50+">50+ employees</option>
                  </select>
                </div>
                
                <Input
                  label="Years in Business"
                  type="number"
                  min="0"
                  value={formData?.yearsInBusiness}
                  onChange={(e) => handleInputChange('yearsInBusiness', e?.target?.value)}
                />
                
                <Input
                  label="Tax ID / EIN"
                  value={formData?.taxId}
                  onChange={(e) => handleInputChange('taxId', e?.target?.value)}
                  description="Your business tax identification number"
                />
              </div>
              
              <div className="mt-6">
                <Input
                  label="Business Specialization"
                  value={formData?.specialization}
                  onChange={(e) => handleInputChange('specialization', e?.target?.value)}
                  description="Describe your primary area of expertise (e.g., Custom Engagement Rings, Vintage Restoration)"
                />
              </div>
            </div>

            {/* Communication Preferences */}
            <div className="bg-card border border-border rounded-lg p-6 elevation-1">
              <h2 className="text-xl font-heading text-foreground mb-6 flex items-center space-x-2">
                <Icon name="Bell" size={20} />
                <span>Communication Preferences</span>
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="email-notifications"
                    checked={formData?.notifications?.email}
                    onChange={(e) => handleNotificationChange('email', e?.target?.checked)}
                    className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  />
                  <label htmlFor="email-notifications" className="text-sm text-foreground">
                    Email Notifications
                  </label>
                </div>
                
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="sms-notifications"
                    checked={formData?.notifications?.sms}
                    onChange={(e) => handleNotificationChange('sms', e?.target?.checked)}
                    className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  />
                  <label htmlFor="sms-notifications" className="text-sm text-foreground">
                    SMS Notifications
                  </label>
                </div>
                
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="project-updates"
                    checked={formData?.notifications?.project_updates}
                    onChange={(e) => handleNotificationChange('project_updates', e?.target?.checked)}
                    className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  />
                  <label htmlFor="project-updates" className="text-sm text-foreground">
                    Project Updates & Client Feedback
                  </label>
                </div>
                
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="marketing-communications"
                    checked={formData?.notifications?.marketing}
                    onChange={(e) => handleNotificationChange('marketing', e?.target?.checked)}
                    className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  />
                  <label htmlFor="marketing-communications" className="text-sm text-foreground">
                    Marketing Communications & Product Updates
                  </label>
                </div>
              </div>
            </div>

            {/* Submit Error */}
            {errors?.submit && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex items-center space-x-3">
                <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
                <p className="text-destructive">{errors?.submit}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/dashboard')}
                disabled={isSaving}
              >
                Cancel
              </Button>
              
              <Button
                type="submit"
                disabled={isSaving}
                iconName={isSaving ? "Loader2" : "Save"}
                className={isSaving ? "animate-spin" : ""}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default ProfileEdit;